import { Scissors, Menu, X } from 'lucide-react'
import { useState } from 'react'

interface HeaderProps {
  scrolled: boolean
}

export default function Header({ scrolled }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const navLinks = [
    { href: '#hem', label: 'Hem' },
    { href: '#tjanster', label: 'Tjänster' },
    { href: '#om-oss', label: 'Om Oss' },
    { href: '#recensioner', label: 'Recensioner' },
    { href: '#galleri', label: 'Galleri' },
    { href: '#kontakt', label: 'Kontakt' },
  ]

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    const element = document.querySelector(href)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
    setMobileMenuOpen(false)
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-dark-900/95 backdrop-blur-md py-3 shadow-lg'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a
            href="#hem"
            onClick={(e) => scrollToSection(e, '#hem')}
            className="flex items-center gap-2 group"
          >
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center group-hover:scale-110 transition-transform overflow-hidden">
              {/* Logo image - lägg till logo.jpg i public-mappen */}
              <img
                src="/logo.jpg"
                alt="VK Studio"
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Om loggan inte finns, visa sax-ikonen istället
                  e.currentTarget.style.display = 'none';
                  (e.currentTarget.parentElement?.querySelector('.fallback-icon') as HTMLElement).style.display = 'flex';
                }}
              />
              <div className="fallback-icon w-full h-full items-center justify-center" style={{ display: 'none' }}>
                <Scissors className="w-5 h-5 text-dark-900" />
              </div>
            </div>
            <span className="font-display text-xl font-semibold text-white tracking-wide">
              VK<span className="text-gold-400">.</span>Studio
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="text-sm font-medium text-gray-300 hover:text-gold-400 transition-colors relative group"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gold-400 transition-all group-hover:w-full" />
              </a>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block">
            <a
              href="https://www.bokadirekt.se/places/vk-studio-54503"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-2.5 bg-gradient-to-r from-gold-500 to-gold-600 text-dark-900 font-semibold rounded-full hover:from-gold-400 hover:to-gold-500 transition-all hover:scale-105 shadow-lg shadow-gold-500/20"
            >
              Boka Tid
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-dark-800/98 backdrop-blur-lg border-t border-white/10 py-4 animate-fade-in">
            <nav className="flex flex-col gap-4 px-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-base font-medium text-gray-300 hover:text-gold-400 transition-colors py-2"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="https://www.bokadirekt.se/places/vk-studio-54503"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 px-6 py-3 bg-gradient-to-r from-gold-500 to-gold-600 text-dark-900 font-semibold rounded-full text-center"
              >
                Boka Tid
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}
